// Public identifier — not a secret, safe to hardcode.
// Created in Google Cloud Console → Map Management.
export const GOOGLE_MAPS_MAP_ID = 'c5209f21d8f1a142410f12f6';

export interface AddressSuggestion {
  placeId: string;
  displayText: string;
  streetNumber?: string;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  lat?: number;
  lng?: number;
}

export const normalizeUnit = (value: string) =>
  value
    .trim()
    .replace(/^(?:ap(?:ar)?t(?:ment)?|unit|suite|ste|#)\s*[#.:-]?\s*/i, '')
    .trim();

export const formatStreetAddress = (address: string, unit: string) => {
  const normalizedUnit = normalizeUnit(unit);
  return normalizedUnit ? `${address.trim()} Unit ${normalizedUnit}` : address.trim();
};

// For the legacy Geocoder API (still used for geocoding plain address strings,
// e.g. in ContextScoreView and PropertyMap batch geocoding).
export function parseGoogleAddressComponents(
  components: google.maps.GeocoderAddressComponent[],
): Partial<AddressSuggestion> {
  const get = (type: string, nameType: 'long_name' | 'short_name' = 'long_name') =>
    components.find((c) => c.types.includes(type))?.[nameType] ?? '';
  return {
    streetNumber: get('street_number'),
    street: get('route'),
    unit: get('subpremise'),
    city: get('locality') || get('sublocality') || get('sublocality_level_1'),
    state: get('administrative_area_level_1', 'short_name'),
    zip: get('postal_code'),
    country: get('country', 'short_name'),
  };
}

// For the new Places API (AutocompleteSuggestion + Place.fetchFields).
// AddressComponent uses longText/shortText instead of long_name/short_name.
export function parseNewPlacesAddressComponents(
  components: google.maps.places.AddressComponent[],
): Partial<AddressSuggestion> {
  const get = (type: string, useShort = false) =>
    components.find((c) => c.types.includes(type))?.[useShort ? 'shortText' : 'longText'] ?? '';
  return {
    streetNumber: get('street_number'),
    street: get('route'),
    unit: get('subpremise'),
    city: get('locality') || get('sublocality') || get('sublocality_level_1'),
    state: get('administrative_area_level_1', true),
    zip: get('postal_code'),
    country: get('country', true),
  };
}

/**
 * Fetch address autocomplete predictions using AutocompleteService.
 * Returns a Promise so callers can use async/await uniformly.
 */
export function fetchAddressSuggestions(
  input: string,
  placesLib: google.maps.PlacesLibrary,
): Promise<google.maps.places.AutocompletePrediction[]> {
  const service = new placesLib.AutocompleteService();
  return new Promise((resolve) => {
    service.getPlacePredictions(
      { input, componentRestrictions: { country: 'us' }, types: ['address'] },
      (predictions, status) => {
        resolve(status === 'OK' ? predictions ?? [] : []);
      },
    );
  });
}

/**
 * Fetch business-name autocomplete suggestions using the new session-token
 * based Autocomplete API (AutocompleteSuggestion), not the legacy
 * AutocompleteService above. Unlike the legacy service, keystrokes within a
 * session are free — the session is only billed once, when the caller picks
 * a result and calls Place.fetchFields() (see placeToBusinessMatch below).
 * No type restriction, since a vendor can be any kind of local business.
 */
export function fetchBusinessSuggestions(
  input: string,
  placesLib: google.maps.PlacesLibrary,
  sessionToken: google.maps.places.AutocompleteSessionToken,
): Promise<google.maps.places.AutocompleteSuggestion[]> {
  return placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    sessionToken,
  }).then((res) => res.suggestions);
}

const PRICE_LEVEL_TO_NUMBER: Record<string, number> = {
  FREE: 0,
  INEXPENSIVE: 1,
  MODERATE: 2,
  EXPENSIVE: 3,
  VERY_EXPENSIVE: 4,
};

export interface PlacesBusinessMatch {
  place_id: string;
  name: string;
  address?: string;
  phone?: string;
  internationalPhoneNumber?: string;
  website?: string;
  googleMapsUri?: string;
  rating?: number;
  reviewCount?: number;
  priceLevel?: number;
  location?: { lat: number; lng: number };
  photoUrl?: string;
  category?: string;
}

const FETCH_FIELDS = [
  'id',
  'displayName',
  'formattedAddress',
  'nationalPhoneNumber',
  'internationalPhoneNumber',
  'websiteURI',
  'googleMapsURI',
  'rating',
  'userRatingCount',
  'priceLevel',
  'location',
  'photos',
  'primaryTypeDisplayName',
];

/**
 * Resolves a picked AutocompleteSuggestion into full Place details and maps
 * it to our vendor field shape. This is the one billed call in the flow —
 * it automatically reuses the session token from fetchBusinessSuggestions
 * (see AutocompleteRequest.sessionToken docs), so the whole search+pick
 * sequence is billed as a single session, not per keystroke.
 */
export async function placeToBusinessMatch(
  suggestion: google.maps.places.AutocompleteSuggestion,
): Promise<PlacesBusinessMatch | null> {
  const prediction = suggestion.placePrediction;
  if (!prediction) return null;
  const { place } = await prediction.toPlace().fetchFields({ fields: FETCH_FIELDS });
  const location = place.location;
  return {
    place_id: place.id,
    name: place.displayName ?? prediction.mainText?.text ?? '',
    address: place.formattedAddress ?? undefined,
    phone: place.nationalPhoneNumber ?? undefined,
    internationalPhoneNumber: place.internationalPhoneNumber ?? undefined,
    website: place.websiteURI ?? undefined,
    googleMapsUri: place.googleMapsURI ?? undefined,
    rating: place.rating ?? undefined,
    reviewCount: place.userRatingCount ?? undefined,
    priceLevel: place.priceLevel ? PRICE_LEVEL_TO_NUMBER[place.priceLevel] : undefined,
    location: location ? { lat: location.lat(), lng: location.lng() } : undefined,
    photoUrl: place.photos?.[0]?.getURI({ maxWidth: 400 }) ?? undefined,
    category: place.primaryTypeDisplayName ?? undefined,
  };
}
